/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { useCallback, useEffect, useState, Dispatch } from 'react';
import { useLocation } from 'react-router';

import { useRootSelector } from '../../../../hooks/useRootSelector';
import { notaryAPI } from '../../../../api/notary';
import { participantAPI } from '../../../../api/participant';
import { isNotaryRoute } from '@utils/routing';
import { get } from '@utils/lodash';
import { SigningActionType } from '@shared/constants/signing';

export const useDocumentSigning = (
  setSelectedDocumentAndFetchPdf: (documentId: string) => void,
  setSigningUrl: Dispatch<string>,
) => {
  const location = useLocation();
  const isNotary = isNotaryRoute(location.pathname);
  const [isSigningModalOpen, setIsSigningModalOpen] = useState(false);
  const { signingRoom, callDetails } = useRootSelector(state => state);
  const { signingAction } = signingRoom;

  const fetchSigningUrl = useCallback(async () => {
    if (signingAction.type === SigningActionType.SIGN && signingAction.document && signingAction.document.id) {
      setIsSigningModalOpen(true);
      setSelectedDocumentAndFetchPdf(signingAction.document.id);

      if (isNotary) {
        const { url } = await notaryAPI.documents.fetchDocumentSigningUrl(callDetails!.id, signingAction.document.id);
        setSigningUrl(url);
        return;
      }

      if (signingAction.participantSessionId) {
        const { url } = await participantAPI.documents.fetchDocumentSigningUrl(
          callDetails!.id,
          signingAction.document.id,
          signingAction.participantSessionId,
        );
        setSigningUrl(url);
      }
    } else {
      setIsSigningModalOpen(false);
    }
  }, [signingAction.participantSessionId, signingAction.type, get(signingAction.document, 'id')]);

  useEffect(() => {
    fetchSigningUrl();
  }, [fetchSigningUrl]);

  return { isSigningModalOpen, setIsSigningModalOpen };
};
